import React, { ReactElement, ReactNode } from 'react';
import { ButtonBase, SxProps } from '@mui/material';
import { COLORS, FONT } from './tokens';

export interface YellowCtaButtonProps {
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
  disabled?: boolean;
  loading?: boolean;

  fullWidth?: boolean;
  height?: number;
  ariaLabel?: string;
  sx?: SxProps;
}

export const YellowCtaButton = ({
  children,
  onClick,
  href,
  disabled = false,
  loading = false,
  fullWidth = false,
  height = 48,
  ariaLabel,
  sx,
}: YellowCtaButtonProps): ReactElement => {
  const isInactive = disabled || loading;

  return (
    <ButtonBase
      component={href && !isInactive ? 'a' : 'button'}
      href={href && !isInactive ? href : undefined}
      onClick={isInactive ? undefined : onClick}
      disabled={isInactive}
      aria-label={ariaLabel}
      aria-busy={loading}
      disableRipple
      sx={{
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: '14px',
        gap: '6px',
        height: `${height}px`,
        px: '20px',
        py: '12px',
        borderRadius: '8px',
        width: fullWidth ? '100%' : 'auto',
        flex: fullWidth ? '1 0 0' : 'none',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        transition: 'background-color 150ms ease, transform 80ms ease',
        backgroundColor: disabled ? COLORS.ctaDisabledBg : COLORS.ctaYellow,
        color: disabled ? COLORS.ctaDisabledText : COLORS.headingDark,
        cursor: isInactive ? 'not-allowed' : 'pointer',
        '&:hover': isInactive
          ? undefined
          : { backgroundColor: COLORS.ctaYellowHover },
        '&:active': isInactive
          ? undefined
          : {
              backgroundColor: COLORS.ctaYellowActive,
              transform: 'scale(0.99)',
            },
        '&:focus-visible': {
          outline: `2px solid ${COLORS.headingDark}`,
          outlineOffset: '2px',
        },
        ...sx,
      }}
    >
      {children}
    </ButtonBase>
  );
};
